<script setup lang="ts">
const { page } = useContent()
const config = useConfig()

const description = page.value?.description || config.value.site.description

useSeoMeta({
  title: `${page.value?.title ?? 'Contact'} - ${config.value.site.name}`,
  ogTitle: page.value?.title,
  description,
  ogDescription: description,
})

// Références pour les champs du formulaire
const email = ref('')
const name = ref('')
const phone = ref('')
const selectedCategories = ref<string>()
const message = ref('')
const website = ref('') // Honeypot anti-spam
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

async function submit() {
  // Réinitialiser les messages
  errorMessage.value = ''
  successMessage.value = ''
  isLoading.value = true

  try {
    const response = await $fetch('/api/contact', {
      method: 'POST',
      body: {
        email: email.value,
        name: name.value,
        phone: phone.value,
        category: selectedCategories.value,
        message: message.value,
        website: website.value,
      },
    })

    successMessage.value = response.message || 'Votre message a été envoyé avec succès'

    // Réinitialiser le formulaire
    email.value = ''
    name.value = ''
    phone.value = ''
    selectedCategories.value = undefined
    message.value = ''
  }
  catch (error) {
    const err = error as { data?: { message?: string }, message?: string }
    errorMessage.value = err.data?.message || err.message || 'Une erreur est survenue lors de l\'envoi du message'
  }
  finally {
    isLoading.value = false
  }
}
</script>

<template>
  <NuxtLayout class="">
    <form
      class="w-full lg:grid lg:grid-cols-2 lg:min-h-[600px] xl:min-h-[800px] mx-auto gap-12 h-full"
      @submit.prevent
    >
      <div class="flex items-center justify-center">
        <div class="grid mx-auto ww-[350px] gap-6">
          <div class="grid gap-2 text-center">
            <h1 class="text-3xl font-bold">
              {{ page?.title }}
            </h1>
            <p class="text-balance text-muted-foreground">
              {{ page?.description }}
            </p>
          </div>
          <div class="grid gap-4">
            <div class="grid gap-2">
              <UiLabel for="email">
                Email*
              </UiLabel>
              <UiInput
                id="email"
                v-model="email"
                type="email"
                placeholder="mon.email@example.com"
                required
              />
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="grid gap-2">
                <UiLabel for="name">
                  Nom
                </UiLabel>
                <UiInput
                  id="name"
                  v-model="name"
                  type="text"
                  placeholder="Votre nom"
                />
              </div>
              <div class="grid gap-2">
                <UiLabel for="phone">
                  Téléphone
                </UiLabel>
                <UiInput
                  id="phone"
                  v-model="phone"
                  type="tel"
                  placeholder="Votre numéro de téléphone"
                />
              </div>
            </div>

            <div class="grid gap-2">
              <UiLabel for="categories">
                Catégories
              </UiLabel>
              <div class="flex flex-wrap gap-2">
                <UiButton
                  v-for="category in page?.categories"
                  :key="category"
                  :value="category"
                  :variant="selectedCategories === category ? 'default' : 'outline'"
                  @click="selectedCategories = category"
                >
                  {{ category }}
                </UiButton>
              </div>
            </div>

            <div class="grid gap-2">
              <UiLabel for="message">
                Message*
              </UiLabel>
              <UiTextarea
                id="message"
                v-model="message"
                required
              />
            </div>

            <input
              v-model="website"
              type="text"
              name="website"
              class="hidden"
              tabindex="-1"
              autocomplete="off"
            >

            <div
              v-if="errorMessage"
              class="text-sm text-destructive"
            >
              {{ errorMessage }}
            </div>
            <div
              v-if="successMessage"
              class="text-sm text-green-600"
            >
              {{ successMessage }}
            </div>

            <UiButton
              type="submit"
              :disabled="isLoading"
              @click="submit()"
            >
              {{ isLoading ? 'Envoi en cours...' : 'Envoyer' }}
            </UiButton>
          </div>
        </div>
      </div>

      <div class="bg-muted">
        <ClientOnly>
          <Map
            :lat="44.4481928"
            :lng="1.4396522"
            :zoom="15"
            :markers="[{ title: 'Quercy Énergies', lat: 44.4481928, lng: 1.4396522, description: '21 Rue Joachim Murat<br>46000 Cahors, France' }]"
            class="h-full"
          />
        </ClientOnly>
      </div>
    </form>
  </NuxtLayout>
</template>
